import { Icon, type IconDefinition } from '@vrplatform/icons';
import { showErrorNotification } from '@vrplatform/theme';
import { sortBy } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  Modal,
  Progress,
  Text,
  Title,
  Transition,
  VisuallyHidden,
  rem,
} from '@mantine/core';
import {
  type ComponentProps,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Button } from '../button';

class ApiError extends Error {
  issues?: {
    message: string;
  }[];
}

type Item = {
  id: string;
  title: string;
  subtitle?: string;
};

type ModalProps = {
  title?: string;
};

type Props = BaseProps &
  ContentProps &
  ErrorProps & {
    modalProps?: ModalProps;
  };

type BaseProps = {
  opened: boolean;
  closeModal: () => void;
  size?: ComponentProps<typeof Modal>['size'];
  zIndex?: ComponentProps<typeof Modal>['zIndex'];
};

type Progress = {
  total: number;
  progress: number;
  errors: (Item & { error: string })[];
};

export const BatchModal = (props: Props) => {
  const [progress, setProgress] = useState<Progress>();

  const {
    opened,
    closeModal: cl,
    size,
    zIndex,
    errorSubtitle,
    errorTitle,
    handleErrors,
    errorSubmitTitle,
    modalProps,
    ...contentProps
  } = props;

  const closeModal = useCallback(() => {
    cl();
    setTimeout(() => {
      setProgress(undefined);
    }, 200);
  }, [cl]);

  const isError = useMemo<boolean>(() => {
    const isFinished = !!(
      progress?.total && progress.progress === progress.total
    );

    return isFinished && !!progress?.errors.length;
  }, [progress?.progress, progress?.total, progress?.errors]);

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      closeButtonProps={{ 'aria-label': 'Close dialog' }}
      title={
        contentProps.type === 'form' ? (
          contentProps.title
        ) : (
          <VisuallyHidden>{contentProps.title}</VisuallyHidden>
        )
      }
      size={isError ? '600' : size}
      radius="md"
      centered
      zIndex={zIndex}
      styles={{
        body: {
          paddingInline: rem(30),
          paddingBottom: rem(30),
        },
        header: {
          paddingBottom: modalProps?.title ? undefined : 0,
          minHeight: 0,
        },
        title: {
          fontWeight: 500,
          paddingLeft: rem(14),
        },
      }}
      {...modalProps}
    >
      {isError && progress ? (
        <ErrorContent
          progress={progress}
          errorSubtitle={errorSubtitle}
          errorTitle={errorTitle}
          closeModal={closeModal}
          errorSubmitTitle={errorSubmitTitle}
          handleErrors={handleErrors as any}
        />
      ) : (
        <BaseContent
          {...contentProps}
          closeModal={closeModal}
          progress={progress}
          setProgress={setProgress}
        />
      )}
    </Modal>
  );
};

type Mode =
  | {
      type: 'confirm' | 'delete';
      children?: undefined;
    }
  | {
      type: 'form';
      children: ReactNode;
    };

export type BatchModalSubmitHandler = () => Promise<{
  items: ({
    promise: () => Promise<any>;
  } & Item)[];
  formIsValid?: boolean;
}>;

type ContentProps = {
  onSubmit: BatchModalSubmitHandler;
  onSettled?: (completed: Item[], failed: Item[]) => void;
  closeModal: () => void;
  submitTitle?: string;
  submitIcon?: IconDefinition;
  submitVariant?: ComponentProps<typeof Button>['variant'];
  submitColor?: ComponentProps<typeof Button>['color'];
  title: string;
  subtitle: string;
  icon?: IconDefinition;
  iconColor?: ComponentProps<typeof Icon>['color'];
} & Mode;

const BaseContent = ({
  closeModal,
  submitTitle,
  title,
  subtitle,
  type,
  icon,
  iconColor,
  submitIcon,
  children,
  progress,
  setProgress,
  onSubmit,
  onSettled,
  submitVariant,
  submitColor,
}: ContentProps & {
  setProgress: React.Dispatch<SetStateAction<Progress | undefined>>;
  progress: Progress | undefined;
}) => {
  const updateProgress = useCallback(
    (item: Item, error?: string) => {
      setProgress((prev) => {
        const cP = prev?.progress || 0;
        const cT = prev?.total || 0;
        const cE = prev?.errors || [];

        return {
          ...prev,
          progress: cP + 1,
          errors: error ? [...cE, { ...item, error }] : cE,
          total: cT,
        };
      });
    },
    [setProgress]
  );

  const setTotal = useCallback(
    (total: number) => {
      setProgress((prev) => ({
        ...prev,
        progress: prev?.progress || 0,
        errors: prev?.errors || [],
        total,
      }));
    },
    [setProgress]
  );

  const submit = async () => {
    setTotal(1);

    const { items, formIsValid = true } = await onSubmit().catch((error) => {
      console.error(error);
      showErrorNotification({
        title: 'Error',
        message: error.message || 'Failed to load items for batch operation',
      });
      return { items: [], formIsValid: false };
    });

    if (!formIsValid) {
      setProgress(undefined);
      return;
    } else {
      setTotal(items.length);
    }

    const results = await Promise.allSettled(
      items.map(async (item) => {
        return await item
          .promise()
          .then(() => {
            updateProgress(item);
          })
          .catch((error: ApiError) => {
            const message =
              error.issues?.[0]?.message ||
              error.message ||
              'An error occurred';
            updateProgress(item, message);
            throw error;
          });
      })
    );

    const calls = results.map((r, i) => ({
      ...items[i],
      result: r,
    }));

    const completed = calls.filter((c) => c.result.status === 'fulfilled');
    const failed = calls.filter((c) => c.result.status === 'rejected');

    if (!failed.length) {
      closeModal();
    }

    onSettled?.(completed, failed);
  };

  const progressValue = useMemo(() => {
    return progress ? (progress.progress / progress.total) * 100 : 0;
  }, [progress]);

  const isFinished = useMemo(() => {
    if (!progress) return false;

    return progress?.progress === progress?.total;
  }, [progress]);

  const isLoading = useMemo(() => {
    return !isFinished && !!progress;
  }, [isFinished, progress]);

  return (
    <>
      {children || (
        <Box
          mb={'xl'}
          sx={() => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          })}
        >
          <Icon
            icon={
              icon || (type === 'confirm' ? 'CheckCircleIcon' : 'TrashIcon')
            }
            color={
              iconColor ||
              (({ colors, primaryColor }) =>
                colors[type === 'confirm' ? primaryColor : 'red'][6])
            }
            size={30}
          />
          <Text fw={500} component="h3" ta="center" size="lg" mt="xl" mb="md">
            {title}
          </Text>
          <Text component="p" c="neutral" ta="center" size="sm">
            {subtitle}
          </Text>
        </Box>
      )}

      <Transition mounted={!!progress} transition="fade-up" duration={300}>
        {(styles) => (
          <Progress
            striped
            animated
            size="sm"
            value={progressValue}
            mb="sm"
            style={styles}
          />
        )}
      </Transition>

      <Group wrap="nowrap">
        <Button
          sx={{
            width: 100,
          }}
          onClick={closeModal}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          leftIcon={submitIcon}
          variant={submitVariant || (type === 'delete' ? 'filled' : 'primary')}
          loading={isLoading}
          color={submitColor || (type === 'delete' ? 'red' : undefined)}
          sx={{
            flex: 1,
            fontWeight: type === 'delete' ? 500 : undefined,
          }}
        >
          {submitTitle || (type === 'delete' ? 'Yes, delete' : 'Yes, submit')}
        </Button>
      </Group>
    </>
  );
};

type ErrorProps = {
  errorTitle: string;
  errorSubtitle: (p: {
    failed: number;
    completed: number;
    total: number;
  }) => string;
  closeModal: () => void;
} & ErrorHandling;

type ErrorHandling =
  | {
      handleErrors: (ids: string[]) => void;
      errorSubmitTitle: string;
    }
  | {
      handleErrors?: undefined;
      errorSubmitTitle?: undefined;
    };

const ErrorContent = ({
  progress,
  errorSubtitle,
  errorTitle,
  closeModal,
  handleErrors,
  errorSubmitTitle,
}: ErrorProps & {
  progress: Progress;
}) => {
  const errors = sortBy(progress.errors, 'title');

  const subtitle = errorSubtitle({
    failed: errors.length,
    completed: progress.progress - errors.length,
    total: progress.total,
  });

  return (
    <>
      <Center mb="lg">
        <Icon
          icon="AlertTriangleIcon"
          size={24}
          color={({ colors, primaryColor }) => colors[primaryColor][6]}
        />
      </Center>
      <Title ta="center" order={4} mb="xs">
        {errorTitle}
      </Title>
      <Text ta="center" mx="auto" c="neutral" maw={400} mb="xl">
        {subtitle}
      </Text>
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.colors.neutral[2]}`,
          borderRadius: theme.radius.md,
          marginBottom: theme.spacing.xl,
          maxHeight: 400,
          overflowY: 'scroll',
        })}
      >
        {errors.map((e, index, arr) => {
          const isLast = index === arr.length - 1;

          return (
            <Group
              key={e.id}
              sx={(theme) => ({
                paddingBlock: theme.spacing.xs,
                paddingInline: theme.spacing.md,
                borderBottom: isLast
                  ? undefined
                  : `1px solid ${theme.colors.neutral[2]}`,
              })}
            >
              <Box
                flex={0.7}
                sx={{
                  flexShrink: 0,
                }}
              >
                <Text fw={500}>{e.title}</Text>
                {e.subtitle && (
                  <Text size="xs" c="neutral">
                    {e.subtitle}
                  </Text>
                )}
              </Box>
              <Text
                flex={1}
                sx={{
                  flexShrink: 0,
                }}
                c="red"
              >
                {e.error}
              </Text>
            </Group>
          );
        })}
      </Box>

      <Group wrap="nowrap">
        <Button
          sx={{
            width: '100%',
          }}
          onClick={closeModal}
          variant={handleErrors ? undefined : 'primary'}
        >
          Continue
        </Button>
        {handleErrors && (
          <Button
            onClick={() => handleErrors(errors.map((e) => e.id))}
            variant={'primary'}
          >
            {errorSubmitTitle}
          </Button>
        )}
      </Group>
    </>
  );
};
